import type { FC, ReactNode } from 'react'
import type { CustomKeycap } from '@/constants/keycaps'
import type { SpecGroup } from '@/constants/specs'

import { Reveal } from '@/components/generals/reveal'
import { HyperStar } from '@/components/landing/brand-marks'

import { CUSTOM_KEYCAPS } from '@/constants/keycaps'

import {
    CONNECTION,
    MATERIALS,
    SIZE_AND_WEIGHT,
    SPECIFICATION
} from '@/constants/specs'

const chipClass = 'group relative grid aspect-square w-full place-items-center rounded-lg border border-white/10 bg-[#0b0c0e] text-[#d4d4d8] transition-colors hover:border-white/25 hover:bg-white/[0.04] hover:text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/60'

const KeycapChip: FC<{ keycap: CustomKeycap }> = ({ keycap }): ReactNode => {
    const Icon = keycap.icon

    const content = (
        <>
            {Icon === 'hyper' ? <HyperStar className='size-4' /> : <Icon className='size-[18px]' strokeWidth={1.75} aria-hidden='true' />}

            <span
                aria-hidden='true'
                className='pointer-events-none absolute bottom-[calc(100%_+_8px)] left-1/2 z-20 -translate-x-1/2 rounded-md border border-white/10 bg-[#16171a] px-2 py-1 font-sans text-[12px] whitespace-nowrap text-white opacity-0 shadow-[0_8px_24px_rgb(0_0_0/0.5)] transition-opacity duration-150 group-hover:opacity-100 group-focus-visible:opacity-100'
            >
                {keycap.name}
            </span>
        </>
    )

    if (keycap.href) {
        return (
            <a
                href={keycap.href}
                target='_blank'
                rel='noopener noreferrer'
                aria-label={`${keycap.name} (opens in a new tab)`}
                className={chipClass}
            >
                {content}
            </a>
        )
    }

    return (
        <span
            role='img'
            tabIndex={0}
            aria-label={keycap.name}
            className={chipClass}
        >
            {content}
        </span>
    )
}

const SpecTable: FC<{ group: SpecGroup }> = ({ group }): ReactNode => (
    <div>
        <h3 className='font-mono text-[12px] font-medium tracking-[0.12em] text-white uppercase'>
            {group.title}
        </h3>

        <dl className='mt-4 border-t border-white/10'>
            {group.specs.map(spec => (
                <div
                    key={spec.label}
                    className='flex items-baseline justify-between gap-6 border-b border-white/10 py-3 font-mono text-[12px] leading-[15px] tracking-[0.1em] uppercase'
                >
                    <dt className='shrink-0 text-[#77787c]'>
                        {spec.label}
                    </dt>

                    <dd className='text-right font-medium text-white'>
                        {spec.value}
                    </dd>
                </div>
            ))}
        </dl>
    </div>
)

const Credits: FC<{ className?: string }> = ({ className }): ReactNode => (
    <p className={className}>
        Designed by Raycast and Engineered by NuPhy
        <br />
        Customized by Nichlas Wærnes Andersen and Tim Van Damme
    </p>
)

const creditsClass = 'font-mono text-[12px] leading-[19px] text-[#5d5e62]'

const SpecsSection: FC = (): ReactNode => {
    return (
        <section id='specs' aria-labelledby='specs-title' className='px-6 pt-16 pb-20 sm:pt-24 sm:pb-28'>
            <h2 id='specs-title' className='sr-only'>
                Technical specifications
            </h2>

            <div className='mx-auto grid max-w-[960px] gap-x-[60px] gap-y-14 md:grid-cols-2'>
                <Reveal>
                    <h3 className='font-mono text-[12px] font-medium tracking-[0.12em] text-white uppercase'>
                        Custom keycaps
                    </h3>

                    <ul className='mt-5 grid grid-cols-7 gap-2 sm:gap-2.5'>
                        {CUSTOM_KEYCAPS.map(keycap => (
                            <li key={keycap.name}>
                                <KeycapChip keycap={keycap} />
                            </li>
                        ))}
                    </ul>
                </Reveal>

                <Reveal>
                    <SpecTable group={SIZE_AND_WEIGHT} />
                </Reveal>

                <Reveal>
                    <SpecTable group={SPECIFICATION} />
                    <Credits className={`${creditsClass} mt-20 hidden md:block`} />
                </Reveal>

                <Reveal className='flex flex-col gap-14'>
                    <SpecTable group={CONNECTION} />
                    <SpecTable group={MATERIALS} />
                </Reveal>

                <Credits className={`${creditsClass} md:hidden`} />
            </div>
        </section>
    )
}

export default SpecsSection
